import { Spinner, Typography } from '@material-tailwind/react'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import TableLayout from '../../../layouts/pagesLayouts/TableLayout'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { isIterableArray, saveGraphToPicture, toFormatPrice } from '../../../utils/utils'
import { useViewConsumptionByMonthByDayPriceQuery } from '../../../store/auth/authService'
import { classCharContainerHeight, isShowInLegend, topLabels, dayFormatChange, dayFormatFilter, dayFormatView, monthFormatFilter, isData } from '../common'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/store'
import FilterByMonth from '../viewConsumption/FilterByMonth'
import GraphConsumption from '../viewConsumption/GraphConsumption'
import axios from 'axios'
import { baseApi } from '../../../ConfigApi'
import { errorToast } from '../../../components/toasts/toasts'
import fileDownload from 'js-file-download'
import { chartColors } from '../../../utils/variables'
import { ConsByMonthDayFormType } from '../common.types'

const GRAPH_ID = "js-cons-comparison-by-day-price-graph"

const ViewByDayPrice = ({ periodDay, setPeriodDay, periodMonth, setPeriodMonth, isMobile }: {
    periodDay: any,
    setPeriodDay: any,
    setPeriodMonth: any,
    periodMonth: string,
    isMobile: boolean
}) => {
    const { t } = useTranslation()
    const { param_contract_id } = useParams<{
        param_contract_id: string;
    }>()

    const { token } = useSelector((state: RootState) => state.auth)
    const { or_number } = useSelector((state: RootState) => state.contract)


    const [sendForm, setSendForm] = useState<ConsByMonthDayFormType>({
        contract_id: Number(param_contract_id) || 0,
        period: periodMonth,
        day: Number(moment(periodDay, dayFormatFilter).format('DD'))
    });

    const { currentData, error, isFetching } = useViewConsumptionByMonthByDayPriceQuery<any>(sendForm, {
        skip: periodMonth === '',
    })

    const uniqueKeys = currentData?.legendKeys?.map((i: string) => {
        return ({
            key: i,
            show: true,
        })
    })
    const [legend, setLegend] = useState([]);
    useEffect(() => {
        if (isIterableArray(uniqueKeys) && !isIterableArray(legend)) {
            setLegend(uniqueKeys)
        }
    }, [uniqueKeys, legend]);



    const labels = Array.from(Array(24).keys()).map(item => ++item)

    const datachar = useCallback(
        () => {
            const data = currentData?.formatted
            if (!data) return
            const datasets = []
            if (isData(data.rdn) && isShowInLegend(legend, 'rdn')) datasets.push({
                animation: false,
                transitions: false,
                order: 4,
                label: t('analytics.tarifs_rdn'),
                data: data.rdn,
                backgroundColor: chartColors.rdn,
                borderRadius: 4,
                spanGaps: false,
                stack: 'Stack1',
                datalabels: {
                    display: false,
                    align: 'center',
                    // rotation: '-90',
                    anchor: 'center',
                    formatter: function (value: any) {
                        return `${toFormatPrice(value)}`;
                    },
                    color: function (context: any) {
                        if (!context?.dataset?.data?.[context?.dataIndex]) return '#00000000'
                        return '#444248';
                    },
                    font: {
                        weight: 500,
                        size: 10,
                        family: 'Inter'
                    }
                }

            })

            if (isData(data.transfer) && isShowInLegend(legend, 'transfer')) datasets.push({
                animation: false,
                transitions: false,
                order: 3,
                label: t('analytics.tarifs_transfer'),
                data: data.transfer,
                backgroundColor: chartColors.transfer,
                borderRadius: 4,
                spanGaps: true,
                stack: 'Stack1',
                datalabels: {
                    display: false,
                    align: 'center',
                    // rotation: '-90',
                    anchor: 'center',
                    formatter: function (value: any) {
                        return `${toFormatPrice(value)}`;
                    },
                    color: function (context: any) {
                        if (!context?.dataset?.data?.[context?.dataIndex]) return '#00000000'
                        return '#444248';
                    },
                    font: {
                        weight: 500,
                        size: 10,
                        family: 'Inter'
                    }
                }
            })

            if (isData(data.class1) && isShowInLegend(legend, 'class1')) datasets.push({
                animation: false,
                transitions: false,
                order: 2,
                label: t('analytics.tarifs_class1'),
                data: data.class1,
                backgroundColor: chartColors.class1,
                borderRadius: 4,
                spanGaps: true,
                stack: 'Stack1',
                datalabels: {
                    display: false,
                    align: 'center',
                    // rotation: '-90',
                    anchor: 'center',
                    formatter: function (value: any) {
                        return `${toFormatPrice(value)}`;
                    },
                    color: function (context: any) {
                        if (!context?.dataset?.data?.[context?.dataIndex]) return '#00000000'
                        return '#444248';
                    },
                    font: {
                        weight: 500,
                        size: 10,
                        family: 'Inter'
                    }
                }
            })
            if (isData(data.class2) && isShowInLegend(legend, 'class2')) datasets.push({
                animation: false,
                transitions: false,
                order: 1,
                label: t('analytics.tarifs_class2'),
                data: data.class2,
                backgroundColor: chartColors.class2,
                borderRadius: 4,
                spanGaps: true,
                stack: 'Stack1',
                datalabels: {
                    display: false,
                    align: 'center',
                    // rotation: '-90',
                    anchor: 'center',
                    formatter: function (value: any) {
                        return `${toFormatPrice(value)}`;
                    },
                    color: function (context: any) {
                        if (!context?.dataset?.data?.[context?.dataIndex]) return '#00000000'
                        return '#fff';
                    },
                    font: {
                        weight: 500,
                        size: 10,
                        family: 'Inter'
                    }
                }
            })


            if (data) return {
                labels: labels,
                datasets: datasets
            }
        },
        [currentData?.formatted, isMobile, legend],
    )

    const setFormForFetch = (values: any) => {
        const newMonthPeriod = moment(values.period, dayFormatFilter).format(monthFormatFilter)
        const newday = moment(values.period, dayFormatFilter).format('DD')
        setPeriodMonth(newMonthPeriod)
        setPeriodDay(values.period)
        setSendForm({
            day: Number(newday),
            period: newMonthPeriod,
            contract_id: Number(param_contract_id)
        })
    }

    const titleTooltipFormatter = (val: string) => {
        return ''
    }

    const saveExcel = (callback?: any) => {
        axios.post(`${baseApi}/api/analitic/build-excel-report-type-4`, {
            ...sendForm,
            typeReport: 6,
        },
            {
                responseType: 'blob',
                headers: {
                    'Authorization': token,
                }
            })
            .then((response: any) => {
                if (response.data.res === 'Error') {
                    errorToast({
                        title: t('analytics.build_excel_report'),
                        text: t('analytics.build_excel_report_error')
                    })
                }
                else {
                    fileDownload(response.data, `${t('analytics.build_excel_report_6')} ${sendForm.day}-${sendForm.period}.xlsx`)
                }
            })
            .catch(error => {
                console.error(error);
                errorToast({
                    title: t('analytics.build_excel_report'),
                    text: t('analytics.build_excel_report_error')
                })
            })
            .finally(() => {
                if (callback) callback()
            })
    }

    return (
        <div className={`transition-opacity delay-[150ms] duration-[250ms]`}>
            <FilterByMonth
                formatView={dayFormatView}
                formatFilter={dayFormatFilter}
                formatChange={dayFormatChange}
                value={{
                    period: periodDay,
                }}
                isLoadingFetch={false}
                fetchPage={setFormForFetch}
                savePicture={() => {
                    saveGraphToPicture(GRAPH_ID, `${t('or')} ${or_number} - ${t('analytics.analytics_comparison_by_year_title')} ${periodDay}.png`)
                }}
                saveExcel={saveExcel}
                isForSave={!!currentData?.isForSave}
            />
            {

                currentData ?
                    <>
                        <div className='bg-white border-2 border-gray-200 rounded-[16px] lap:rounded-[24px] p-[24px] lap:p-[32px]'>
                            <Typography variant='lead' className='text-gray-800 text-center mb-[24px] tab:mb-[32px] text-[20px] lap:text-[24px] text-left'>
                                {t('analytics.tarifs_by_day_title')}
                            </Typography>
                            <div className={classCharContainerHeight}>
                                <GraphConsumption
                                    graphId={GRAPH_ID}
                                    titleTooltipFormatter={titleTooltipFormatter}
                                    datachar={datachar()}
                                    fixedCount={2}
                                    plugins={isMobile ? [] : [topLabels(2)]}
                                />
                            </div>
                            {
                                !!currentData?.isForSave &&
                                    (isIterableArray(uniqueKeys) && isIterableArray(legend))
                                    ?
                                    <div className=' mt-[24px] flex gap-[8px] lap:gap-x-[16px] flex-wrap justify-start'>
                                        {
                                            legend.map((item: any, index: number) => (
                                                <div key={index} className='flex gap-[2px] items-center cursor-pointer' onClick={() => {
                                                    setLegend((prev: any) => prev.map((i: any) => ({
                                                        ...i,
                                                        show: item.key === i.key ? !i.show : i.show
                                                    })))
                                                }}>
                                                    <span className={`block rounded w-[10px] h-[10px]  `} style={{ backgroundColor: item.show ? chartColors?.[item.key] : '#eee' }}></span>
                                                    <Typography variant='lead' className={`${item.show ? 'text-gray-700' : 'text-gray-400'}  font-medium text-[12px] lap:text-[14px]`}>
                                                        {t(`analytics.legend_${item.key}`)}
                                                    </Typography>
                                                </div>
                                            ))
                                        }
                                    </div>
                                    :
                                    <div className='min-h-[45px]' />
                            }
                        </div>
                    </>
                    :
                    isFetching ?
                        <div className={`${classCharContainerHeight} bg-white border-2 border-gray-200 rounded-[16px] lap:rounded-[24px] p-[24px] lap:p-[32px] flex justify-center items-center`}>
                            <Spinner />
                        </div>
                        :
                        error ?
                            <TableLayout>
                                <Typography variant='lead' className='text-[16px] lap:text-[20px] font-medium text-center my-[40px]'>
                                    {t('tables.error_on_loading')}
                                </Typography>
                            </TableLayout>
                            :
                            ''
            }
        </div>
    )
}

export default ViewByDayPrice